import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useStableCallback } from '../hooks/useStableCallback';
import { Container, Row, Col, Card, Button, Alert, Dropdown, Form, Modal, ListGroup, Badge, Collapse } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getAggregatedTickers, updateTickerPrices, getTickers, getAllAccountsWithAggregate, recalculateAccountShields } from '../services/api';
import { initializeColumnConfig, saveAsDefaultColumnConfig, resetToDefaultColumnConfig, getDefaultColumnConfig } from '../utils/columnPreferences';
import { useTickerUpdates } from '../hooks/useTickerUpdates';
import TickerTable from '../components/TickerTable';
import ColumnVisibilityDropdown from '../components/ColumnVisibilityDropdown';
import TreeMapContainer from '../components/TreeMapContainer';
import PieChartModal from '../components/PieChartModal';
import BalanceHistoryModal from '../components/BalanceHistoryModal';
import PerformanceChartModal from '../components/PerformanceChartModal';
import TransactionModal from '../components/TransactionModal';
import TickerTransactionHistoryModal from '../components/TickerTransactionHistoryModal';
import { ThemeContext } from '../contexts/ThemeContext';
import { createLogger } from '../utils/debugLogger';
import { applyThemeOverridesToTickers } from '../utils/themeOverrides';
import { applyBaseOverridesToTickers } from '../utils/baseOverrides';

const logger = createLogger(
  'aggregatedTickersPage',
  'Aggregated tickers page refreshes and analytics',
  false,
  'DEBUG'
);

// Fields that are edited locally (via user action, e.g. CategorySelect) rather than
// refreshed by the price-polling/summary endpoint. A poll response is just a snapshot of
// whatever the backend returned for the in-flight request that triggered it -- if the user
// edits one of these fields while an older poll is still in flight, that poll's stale value
// must not be allowed to stomp the just-saved local value when it resolves and merges in.
const POLL_PRESERVED_TICKER_FIELDS = ['shields', 'category'];

// Merges a freshly-polled/fetched ticker list with the current local ticker state, carrying
// forward POLL_PRESERVED_TICKER_FIELDS from the existing (matched by symbol + account_id)
// ticker instead of letting the new snapshot overwrite them.
const mergeTickersPreservingLocalFields = (newTickers, currentTickers = []) => {
  return newTickers.map(newTicker => {
    const existingTicker = currentTickers.find(current =>
      current.symbol === newTicker.symbol &&
      current.account_id === newTicker.account_id
    );

    if (!existingTicker) {
      return newTicker;
    }

    const preserved = {};
    POLL_PRESERVED_TICKER_FIELDS.forEach(field => {
      if (existingTicker[field] !== undefined) {
        preserved[field] = existingTicker[field];
      }
    });

    return { ...newTicker, ...preserved };
  });
};

const alignThemeColumnAfterCategory = (order = []) => {
  if (!Array.isArray(order)) {
    return [];
  }

  const aligned = order.filter(column => typeof column === 'string');
  const categoryIndex = aligned.indexOf('category');
  const themeIndex = aligned.indexOf('theme');
  const baseIndex = aligned.indexOf('base');

  if (categoryIndex === -1 && themeIndex === -1 && baseIndex === -1) {
    return aligned;
  }

  if (themeIndex === -1) {
    if (categoryIndex === -1) {
      aligned.push('theme');
    } else {
      aligned.splice(categoryIndex + 1, 0, 'theme');
    }
  }

  if (categoryIndex !== -1 && themeIndex !== categoryIndex + 1) {
    const [themeColumn] = aligned.splice(aligned.indexOf('theme'), 1);
    const refreshedCategoryIndex = aligned.indexOf('category');
    if (refreshedCategoryIndex === -1) {
      aligned.push(themeColumn);
    } else {
      aligned.splice(refreshedCategoryIndex + 1, 0, themeColumn);
    }
  }

  const insertBaseAfter = (anchor) => {
    const anchorIndex = aligned.indexOf(anchor);
    if (anchorIndex === -1) {
      aligned.push('base');
    } else {
      aligned.splice(anchorIndex + 1, 0, 'base');
    }
  };

  if (baseIndex !== -1) {
    aligned.splice(aligned.indexOf('base'), 1);
    if (aligned.includes('theme')) {
      insertBaseAfter('theme');
    } else {
      insertBaseAfter('category');
    }
  } else {
    if (aligned.includes('theme')) {
      insertBaseAfter('theme');
    } else if (aligned.includes('category')) {
      insertBaseAfter('category');
    } else {
      aligned.push('base');
    }
  }

  return aligned;
};

const defaultColumnOrder = [
  'symbol',
  'strategy',
  'stop',
  'actions',
  'name',
  'rating',
  'source',
  'category',
  'theme',
  'base',
  'entry',
  'shares',
  'cost_basis',
  'total_cost',
  'current_price',
  'pre_close',
  'current_value',
  'high', // High column to show new highs since purchase
  'today_gain_amount',
  'today_gain_percentage',
  'total_gain',
  'total_gain_pct',
  'trade_gain',
  'trade_gain_pct',
  'days_held',
  'position_size',
  'armor',
  'risk_gain',
  'risk_gain_amount'
];

const defaultVisibleColumns = {
  strategy: true,
  stop: true,
  name: false,
  rating: true,
  source: true,
  category: true,
  theme: true,
  base: false,
  entry: true,
  shares: true,
  cost_basis: false,
  total_cost: false,
  pre_close: false,
  current_price: false,
  current_value: true,
  high: true, // High column visible by default to show new highs
  today_gain_amount: false,
  today_gain_percentage: true,
  total_gain: false,
  total_gain_pct: true,
  trade_gain: true,
  trade_gain_pct: true,
  days_held: true,
  position_size: true,
  armor: true,
  risk_gain: true,
  risk_gain_amount: true,
  actions: true
};

// Custom styles for the component
const customStyles = `
    /* Simple spin animation for refresh icon */
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
    .spin {
        animation: spin 1s linear infinite;
        display: inline-block;
    }

    .table-row-hover tbody tr {
        transition: background-color 0.15s ease-in-out;
    }
    .table-row-hover tbody tr:hover {
        background-color: rgba(0, 123, 255, 0.1) !important;
        cursor: pointer;
    }
    .tickers-empty-icon {
        color: #6c757d;
        opacity: 0.6;
    }
    .hover-bg-light:hover {
        background-color: #f8f9fa;
    }
    
    /* Enhanced card styling for maximum contrast and prominence */
    .tickers-card {
        background-color: var(--card-bg) !important;
        border: 2px solid var(--border-color) !important;
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        transition: all 0.3s ease-in-out;
        position: relative;
    }
    
    .tickers-card:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
        transform: translateY(-2px);
    }
    
    /* Light theme card enhancements - much more contrast */
    [data-theme="light"] .tickers-card {
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .tickers-card:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme card enhancements - much more contrast */
    [data-theme="dark"] .tickers-card {
        background-color: #1f2937 !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5) !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .tickers-card:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Enhanced table styling for maximum contrast and prominence */
    .tickers-table-container {
        background-color: var(--seasonal-bg, var(--card-bg)) !important;
        border: 2px solid var(--border-color) !important;
        border-radius: 8px 8px 0 0 !important; /* Only top corners rounded */
        box-shadow: 0 4px 16px var(--shadow-color) !important;
        overflow: hidden !important;
        transition: all 0.3s ease-in-out;
        height: 100%;
        min-height: 0;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
    }
    .tickers-table-container .table-responsive {
        height: 100%;
        min-height: 0;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
        overflow: unset !important;
    }
    .tickers-table-container .tickers-table {
        height: 100%;
        min-height: 0;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
    }
    
    .tickers-table-container:hover {
        box-shadow: 0 8px 24px var(--shadow-color) !important;
    }
    
    /* Light theme table enhancements */
    [data-theme="light"] .tickers-table-container {
        background-color: var(--seasonal-bg, #ffffff) !important;
        border: 2px solid #d1d5db !important;
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.15) !important;
    }
    
    [data-theme="light"] .tickers-table-container:hover {
        box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.25) !important;
        border-color: #9ca3af !important;
    }
    
    /* Dark theme table enhancements */
    [data-theme="dark"] .tickers-table-container {
        background-color: var(--seasonal-bg, #1f2937) !important;
        border: 2px solid #4b5563 !important;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.05), 0 4px 20px rgba(0, 0, 0, 0.5) !important;
    }
    
    [data-theme="dark"] .tickers-table-container:hover {
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.6) !important;
        border-color: #6b7280 !important;
        background-color: #374151 !important;
    }
    
    /* Enhanced table header styling */
    .tickers-table-container .tickers-table thead th,
    .tickers-table-container .tickers-table-header {
        background-color: var(--bg-secondary) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        transition: background-color 0.2s ease-in-out;
    }
    
    .tickers-table-container .tickers-table thead th:hover,
    .tickers-table-container .tickers-table-header:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer;
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table thead th,
    [data-theme="light"] .tickers-table-container .tickers-table-header {
        background-color: #f8f9fa !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table thead th:hover,
    [data-theme="light"] .tickers-table-container .tickers-table-header:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table thead th,
    [data-theme="dark"] .tickers-table-container .tickers-table-header {
        background-color: #374151 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table thead th:hover,
    [data-theme="dark"] .tickers-table-container .tickers-table-header:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced table row styling with hover effects - lively but without flicker */
    .tickers-table-container .tickers-table tbody tr {
        border-bottom: 1px solid var(--border-color) !important;
        transition: background-color 0.2s ease-in-out, transform 0.2s ease, box-shadow 0.2s ease !important;
        font-size: 0.95rem !important;
    }
    
    .tickers-table-container .tickers-table tbody tr:hover {
        background-color: var(--hover-bg) !important;
        cursor: pointer !important;
        /* Add light movement and glow without injecting borders to prevent flicker */
        transform: translateY(-2px) !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important, inset 0 -2px 0 var(--border-color) !important; /* outer + subtle bottom highlight */
        border-left: none !important;
        border-right: none !important;
    }

    /* Theme-specific brighter bottom line on hover */
    [data-theme="light"] .tickers-table-container .tickers-table tbody tr:hover {
        box-shadow: 0 4px 12px rgba(13, 110, 253, 0.2) !important, inset 0 -2px 0 #b8daff !important; /* soft blue highlight */
    }
    [data-theme="dark"] .tickers-table-container .tickers-table tbody tr:hover {
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important, inset 0 -2px 0 #3b82f6 !important; /* bright blue for dark */
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table tbody tr:hover {
        background-color: #e3f2fd !important;
        box-shadow: 0 4px 12px rgba(13, 110, 253, 0.2) !important;
        border-left-color: #0d6efd !important;
        border-right-color: #0d6efd !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table tbody tr:hover {
        background-color: #1e3a8a !important;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
        border-left-color: #3b82f6 !important;
        border-right-color: #3b82f6 !important;
    }
    
    /* Enhanced table cell styling - compact, without scaling on hover */
    .tickers-table-container .tickers-table tbody td {
        padding: 4px 6px !important;
        border-bottom: 1px solid var(--border-color) !important;
        vertical-align: middle !important;
        transition: color 0.2s ease-in-out !important;
    }
    
    .tickers-table-container .tickers-table tbody tr:hover td {
        color: var(--text-primary) !important;
        transform: none !important;
    }
    [data-theme="light"] .tickers-table-container .tickers-table tbody tr:hover td {
        color: #0d6efd !important;
    }
    [data-theme="dark"] .tickers-table-container .tickers-table tbody tr:hover td {
        color: #93c5fd !important;
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table tbody td {
        border-bottom: 1px solid #e5e7eb !important;
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table tbody tr:hover td {
        color: #1e40af !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table tbody td {
        border-bottom: 1px solid #374151 !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table tbody tr:hover td {
        color: #93c5fd !important;
    }
    
    /* Enhanced total row styling - match header styling exactly */
    .tickers-table-container .tickers-table .table-total {
        background-color: var(--bg-secondary) !important;
        border-top: 2px solid var(--border-color) !important;
        border-bottom: 2px solid var(--border-color) !important;
        font-weight: 600 !important;
        color: var(--text-primary) !important;
    }
    
    .tickers-table-container .tickers-table .table-total td {
        background-color: var(--bg-secondary) !important;
        color: var(--text-primary) !important;
        padding: 6px 6px !important;
        border-bottom: none !important;
    }
    
    .tickers-table-container .tickers-table .table-total:hover {
        background-color: var(--hover-bg) !important;
        cursor: default !important;
        transform: none !important;
        box-shadow: none !important;
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table .table-total {
        background-color: #f8f9fa !important;
        border-top: 2px solid #d1d5db !important;
        border-bottom: 2px solid #d1d5db !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table .table-total td {
        background-color: #f8f9fa !important;
        color: #374151 !important;
    }
    
    [data-theme="light"] .tickers-table-container .tickers-table .table-total:hover {
        background-color: #e9ecef !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table .table-total {
        background-color: #374151 !important;
        border-top: 2px solid #4b5563 !important;
        border-bottom: 2px solid #4b5563 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table .table-total td {
        background-color: #374151 !important;
        color: #f9fafb !important;
    }
    
    [data-theme="dark"] .tickers-table-container .tickers-table .table-total:hover {
        background-color: #4b5563 !important;
    }
    
    /* Enhanced table styling - consistent with account details */
    .tickers-table-container .tickers-table {
        background-color: var(--card-bg) !important;
        border: none !important;
        margin-bottom: 0 !important;
    }
    
    /* Remove gap between last row and bottom of table container */
    .tickers-table-container .table-responsive {
        margin-bottom: 0 !important;
    }
    
    /* Less prominent delete button */
    .btn-delete-subtle {
        color: #6c757d !important;
        border-color: #6c757d !important;
        background-color: transparent !important;
        font-size: 0.875rem !important;
        padding: 0.25rem 0.5rem !important;
    }
    
    .btn-delete-subtle:hover {
        color: #dc3545 !important;
        border-color: #dc3545 !important;
        background-color: rgba(220, 53, 69, 0.1) !important;
    }
    
    [data-theme="dark"] .btn-delete-subtle {
        color: #9ca3af !important;
    }
    
    /* CSS Variables for theme support */
    :root {
        --card-bg: #ffffff;
        --border-color: #d1d5db;
        --shadow-color: rgba(0, 0, 0, 0.1);
        --text-primary: #374151;
        --bg-secondary: #f8f9fa;
        --hover-bg: #f1f5f9;
    }
    
    [data-theme="dark"] {
        --card-bg: #1f2937;
        --border-color: #4b5563;
        --shadow-color: rgba(0, 0, 0, 0.3);
        --text-primary: #f9fafb;
        --bg-secondary: #374151;
        --hover-bg: #4b5563;
    }
    
    [data-theme="dark"] .btn-delete-subtle:hover {
        color: #f87171 !important;
        border-color: #f87171 !important;
        background-color: rgba(248, 113, 113, 0.1) !important;
    }
    
    .ticker-quick-filter {
        position: relative;
    }
    
    .ticker-quick-filter .input-clear-button {
        position: absolute;
        right: 0.3rem;
        top: 50%;
        transform: translateY(-50%);
        border: none;
        background: transparent;
        color: #6c757d;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        cursor: pointer;
    }
    
    .ticker-quick-filter .input-clear-button:disabled {
        cursor: default;
        opacity: 0.3;
    }
    
    [data-theme="dark"] .ticker-quick-filter .input-clear-button {
        color: #9ca3af;
    }
    
    .ticker-quick-filter .input-search-icon {
        position: absolute;
        left: 0.35rem;
        top: 50%;
        transform: translateY(-50%);
        color: #6c757d;
        pointer-events: none;
    }
    
    [data-theme="dark"] .ticker-quick-filter .input-search-icon {
        color: #9ca3af;
    }
`;

function AggregatedTickers() {
  const {
    effectiveTheme,
    weatherThemeEnabled,
    selectedWeatherTheme,
  } = useContext(ThemeContext);
  const isDark = effectiveTheme === 'dark';
  const navigate = useNavigate();

  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState('');
  const [, setRefreshLoading] = useState(false);
  const [showTreeMapModal, setShowTreeMapModal] = useState(() => {
    const stored = localStorage.getItem('treemapShowAggregated');
    return stored === 'true';
  });
  const [showPieChartModal, setShowPieChartModal] = useState(false);
  const [showBalanceHistoryModal, setShowBalanceHistoryModal] = useState(false);
  const [shieldsRefreshing, setShieldsRefreshing] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [historySaveMessage, setHistorySaveMessage] = useState('');
  const [summary, setSummary] = useState({
    total_value: 0,
    total_cash: 0,
    today_gain_amount: 0,
    today_gain_percentage: 0,
    total_gain_amount: 0,
    total_gain_percentage: 0,
    ytd_gain_amount: 0,
    ytd_gain_percentage: 0,
    previous_day_balance: null
  });
  
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionInitialData, setTransactionInitialData] = useState(null);
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [showAccountSelectionModal, setShowAccountSelectionModal] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [availableTickers, setAvailableTickers] = useState([]);
  const [marketInfo, setMarketInfo] = useState(null);
  const [showTickerHistoryModal, setShowTickerHistoryModal] = useState(false);
  const [selectedTickerSymbol, setSelectedTickerSymbol] = useState(null);
  const [showSaveDefaultModal, setShowSaveDefaultModal] = useState(false);
  const [showResetDefaultModal, setShowResetDefaultModal] = useState(false);
  const [aggregatedPrevBalance, setAggregatedPrevBalance] = useState(null);
  useEffect(() => {
    localStorage.setItem('treemapShowAggregated', showTreeMapModal ? 'true' : 'false');
  }, [showTreeMapModal]);

  // Fetch aggregated previous day balance from backend to align treemap with account basis
  useEffect(() => {
    let isMounted = true;
    const fetchAggregatedPrevBalance = async () => {
      try {
        const response = await getAllAccountsWithAggregate();
        const accounts = response?.data?.accounts || [];
        const totalPrevBalance = accounts.reduce((sum, account) => sum + (account.previous_day_balance || 0), 0);
        if (isMounted && !Number.isNaN(totalPrevBalance)) {
          setAggregatedPrevBalance(totalPrevBalance);
        }
      } catch (err) {
        logger.error('Failed to fetch aggregated previous_day_balance for treemap', err);
      }
    };

    fetchAggregatedPrevBalance();
    return () => {
      isMounted = false;
    };
  }, []);
  
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const [columnOrder, setColumnOrder] = useState(defaultColumnOrder);
  const [columnsInitialized, setColumnsInitialized] = useState(false);

  const sanitizeVisibleColumns = React.useCallback((columns = {}) => {
    return { ...defaultVisibleColumns, ...columns };
  }, []);

  const clearLegacyColumnStorage = React.useCallback(() => {
    ['visibleColumns-allTickers', 'columnOrder-allTickers', 'visibleColumns-aggregatedTickers', 'columnOrder-aggregatedTickers']
      .forEach(key => localStorage.removeItem(key));
  }, []);

  const clearColumnStorage = React.useCallback(() => {
    ['visibleColumns-tickers', 'columnOrder-tickers'].forEach(key => localStorage.removeItem(key));
    clearLegacyColumnStorage();
  }, [clearLegacyColumnStorage]);

  const safelyUpdateColumnOrder = React.useCallback((newOrder) => {
    // Ensure symbol, strategy, and actions are always included at the right positions
    let safeOrder = [...newOrder];

    // Handle symbol column (always at position 0)
    if (!safeOrder.includes('symbol')) {
      safeOrder = ['symbol', ...safeOrder];
    } else if (safeOrder[0] !== 'symbol') {
      const filteredOrder = safeOrder.filter(col => col !== 'symbol');
      safeOrder = ['symbol', ...filteredOrder];
    }

    // Ensure strategy column is positioned immediately after symbol
    if (!safeOrder.includes('strategy')) {
      const remaining = safeOrder.filter(col => col !== 'symbol');
      safeOrder = ['symbol', 'strategy', ...remaining];
    } else if (safeOrder[1] !== 'strategy') {
      const remaining = safeOrder.filter(col => col !== 'symbol' && col !== 'strategy');
      safeOrder = ['symbol', 'strategy', ...remaining];
    }

    // Ensure stop column is positioned immediately after strategy. This must
    // agree with TickerTable's own pinning effect (symbol, strategy, stop,
    // actions) -- disagreeing here caused the two of them to fight over
    // stop/actions' position forever, flickering both columns every render.
    if (!safeOrder.includes('stop')) {
      const remaining = safeOrder.filter(col => col !== 'symbol' && col !== 'strategy');
      safeOrder = ['symbol', 'strategy', 'stop', ...remaining];
    } else if (safeOrder[2] !== 'stop') {
      const remaining = safeOrder.filter(col => col !== 'symbol' && col !== 'strategy' && col !== 'stop');
      safeOrder = ['symbol', 'strategy', 'stop', ...remaining];
    }

    // Handle actions column (always immediately after stop)
    if (!safeOrder.includes('actions')) {
      const remaining = safeOrder.filter(col => !['symbol', 'strategy', 'stop'].includes(col));
      safeOrder = ['symbol', 'strategy', 'stop', 'actions', ...remaining];
    } else {
      const remaining = safeOrder.filter(col => !['symbol', 'strategy', 'stop', 'actions'].includes(col));
      safeOrder = ['symbol', 'strategy', 'stop', 'actions', ...remaining];
    }

    // Add total_cost to order if not present
    if (!safeOrder.includes('total_cost')) {
      // Add after cost_basis if it exists
      const costBasisIndex = safeOrder.indexOf('cost_basis');
      if (costBasisIndex !== -1) {
        safeOrder = [
          ...safeOrder.slice(0, costBasisIndex + 1),
          'total_cost',
          ...safeOrder.slice(costBasisIndex + 1)
        ];
      } else {
        // Otherwise, add after shares
        const sharesIndex = safeOrder.indexOf('shares');
        if (sharesIndex !== -1) {
          safeOrder = [
            ...safeOrder.slice(0, sharesIndex + 1),
            'total_cost',
            ...safeOrder.slice(sharesIndex + 1)
          ];
        } else {
          // If all else fails, add at position 5
          safeOrder = [
            ...safeOrder.slice(0, 5),
            'total_cost',
            ...safeOrder.slice(5)
          ];
        }
      }
    }
    // Ensure theme column follows category
    if (!safeOrder.includes('theme')) {
      const categoryIndex = safeOrder.indexOf('category');
      if (categoryIndex !== -1) {
        safeOrder.splice(categoryIndex + 1, 0, 'theme');
      } else {
        safeOrder.push('theme');
      }
    } else {
      safeOrder = alignThemeColumnAfterCategory(safeOrder);
    }

    // Ensure entry column follows base (or theme if base missing)
    if (!safeOrder.includes('entry')) {
      const baseIndex = safeOrder.indexOf('base');
      if (baseIndex !== -1) {
        safeOrder.splice(baseIndex + 1, 0, 'entry');
      } else {
        const themeIndex = safeOrder.indexOf('theme');
        if (themeIndex !== -1) {
          safeOrder.splice(themeIndex + 1, 0, 'entry');
        } else {
          safeOrder.push('entry');
        }
      }
    }

    setColumnOrder(safeOrder);
  }, []);

  const updateColumnPreferencesState = React.useCallback((visible, order) => {
    setVisibleColumns(sanitizeVisibleColumns(visible));
    if (order && order.length > 0) {
      safelyUpdateColumnOrder(order);
    } else {
      safelyUpdateColumnOrder(defaultColumnOrder);
    }
    setColumnsInitialized(true);
  }, [sanitizeVisibleColumns, safelyUpdateColumnOrder]);

  useEffect(() => {
    let isMounted = true;

    const loadColumnPreferences = async () => {
      try {
        const config = await initializeColumnConfig('tickers');
        if (!isMounted) {
          return;
        }

        updateColumnPreferencesState(config.visibleColumns, config.columnOrder);
      } catch (error) {
        logger.error('Failed to initialize column configuration for tickers', error);
        if (!isMounted) {
          return;
        }

        const savedColumns =
          localStorage.getItem('visibleColumns-tickers') ||
          localStorage.getItem('visibleColumns-allTickers') ||
          localStorage.getItem('visibleColumns-aggregatedTickers');
        const savedOrder =
          localStorage.getItem('columnOrder-tickers') ||
          localStorage.getItem('columnOrder-allTickers') ||
          localStorage.getItem('columnOrder-aggregatedTickers');

        if (savedColumns && savedOrder) {
          try {
            updateColumnPreferencesState(JSON.parse(savedColumns), JSON.parse(savedOrder));
          } catch (parseError) {
            logger.error('Failed to parse localStorage column settings', parseError);
            updateColumnPreferencesState(defaultVisibleColumns, defaultColumnOrder);
          }
        } else {
          updateColumnPreferencesState(defaultVisibleColumns, defaultColumnOrder);
        }
      } finally {
        if (isMounted) {
          clearLegacyColumnStorage();
        }
      }
    };

    loadColumnPreferences();

    return () => {
      isMounted = false;
    };
  }, [updateColumnPreferencesState, clearLegacyColumnStorage]);

  // Save column visibility when it changes
  useEffect(() => {
    if (!visibleColumns || Object.keys(visibleColumns).length === 0) {
      return;
    }

    localStorage.setItem('visibleColumns-tickers', JSON.stringify(visibleColumns));
    localStorage.removeItem('visibleColumns-allTickers');
    localStorage.removeItem('visibleColumns-aggregatedTickers');
  }, [visibleColumns]);
  
  // Debug logging for visibleColumns changes
  useEffect(() => {
    logger.debug('[AggregatedTickers] visibleColumns changed', visibleColumns);
    logger.debug('[AggregatedTickers] Risk columns state', {
      risk_gain: visibleColumns.risk_gain,
      risk_gain_amount: visibleColumns.risk_gain_amount
    });
  }, [visibleColumns]);

  // Save column order when it changes
  useEffect(() => {
    if (!columnOrder || columnOrder.length === 0) {
      return;
    }

    localStorage.setItem('columnOrder-tickers', JSON.stringify(columnOrder));
    localStorage.removeItem('columnOrder-allTickers');
    localStorage.removeItem('columnOrder-aggregatedTickers');
  }, [columnOrder]);
  
  // Ensure pre_close column is added to columnOrder (but respect visibility setting)
  useEffect(() => {
    // Update columnOrder if pre_close is missing
    if (!columnOrder.includes('pre_close')) {
      const newOrder = [...columnOrder];
      const currentPriceIndex = newOrder.indexOf('current_price');
      
      if (currentPriceIndex !== -1) {
        // Insert pre_close before current_price
        newOrder.splice(currentPriceIndex, 0, 'pre_close');
        safelyUpdateColumnOrder(newOrder);
      } else {
        // If current_price is not found, add pre_close at a sensible position
        const costBasisIndex = newOrder.indexOf('cost_basis');
        if (costBasisIndex !== -1) {
          newOrder.splice(costBasisIndex + 1, 0, 'pre_close');
          safelyUpdateColumnOrder(newOrder);
        }
      }
    }
    
    // Make sure pre_close exists in visibleColumns (but don't change its visibility)
    if (visibleColumns && !('pre_close' in visibleColumns)) {
      setVisibleColumns(prev => ({
        ...prev,
        pre_close: false // Keep default hidden
      }));
    }
    // Intentionally mount-only: this is a one-time migration to backfill a column that may
    // be missing from a legacy stored columnOrder. Re-running on every columnOrder/
    // visibleColumns change would be redundant once the backfill has happened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Ensure high column is added to columnOrder (but respect visibility setting)
  useEffect(() => {
    // Update columnOrder if high is missing
    if (!columnOrder.includes('high')) {
      const newOrder = [...columnOrder];
      const currentValueIndex = newOrder.indexOf('current_value');
      
      if (currentValueIndex !== -1) {
        // Insert high after current_value
        newOrder.splice(currentValueIndex + 1, 0, 'high');
        safelyUpdateColumnOrder(newOrder);
      } else {
        // If current_value is not found, add high at a sensible position
        const preCloseIndex = newOrder.indexOf('pre_close');
        if (preCloseIndex !== -1) {
          newOrder.splice(preCloseIndex + 1, 0, 'high');
          safelyUpdateColumnOrder(newOrder);
        }
      }
    }
    
    // Make sure high exists in visibleColumns (but don't change its visibility)
    if (visibleColumns && !('high' in visibleColumns)) {
      setVisibleColumns(prev => ({
        ...prev,
        high: true // Keep default visible
      }));
    }
  }, [columnOrder, visibleColumns, safelyUpdateColumnOrder]);

  // Ensure stop column is added to columnOrder (but respect visibility setting).
  // TickerTable pins it to sit right after strategy regardless of where it lands
  // here, but seed it next to strategy anyway so a fresh install doesn't flash-reorder.
  useEffect(() => {
    if (!columnOrder.includes('stop')) {
      const newOrder = [...columnOrder];
      const strategyIndex = newOrder.indexOf('strategy');
      const insertAt = strategyIndex !== -1 ? strategyIndex + 1 : newOrder.length;
      newOrder.splice(insertAt, 0, 'stop');
      safelyUpdateColumnOrder(newOrder);
    }

    // Make sure stop exists in visibleColumns (but don't change its visibility
    // if the user already has a preference for it)
    if (visibleColumns && !('stop' in visibleColumns)) {
      setVisibleColumns(prev => ({
        ...prev,
        stop: true // Keep default visible
      }));
    }
  }, [columnOrder, visibleColumns, safelyUpdateColumnOrder]);

  // Ensure trade_gain/trade_gain_pct columns are added to columnOrder (same Trade$/Trade%
  // columns shown on the Account Details page, linking each ticker to its open trade)
  useEffect(() => {
    const missing = ['trade_gain', 'trade_gain_pct'].filter(col => !columnOrder.includes(col));
    if (missing.length > 0) {
      const newOrder = [...columnOrder];
      const totalGainPctIndex = newOrder.indexOf('total_gain_pct');
      const insertAt = totalGainPctIndex !== -1 ? totalGainPctIndex + 1 : newOrder.length;
      newOrder.splice(insertAt, 0, ...missing);
      safelyUpdateColumnOrder(newOrder);
    }

    // Make sure trade_gain/trade_gain_pct exist in visibleColumns (but don't change visibility
    // if the user already has a preference for them)
    const missingVisible = ['trade_gain', 'trade_gain_pct'].filter(
      col => visibleColumns && !(col in visibleColumns)
    );
    if (missingVisible.length > 0) {
      setVisibleColumns(prev => ({
        ...prev,
        ...Object.fromEntries(missingVisible.map(col => [col, true])) // Keep default visible
      }));
    }
  }, [columnOrder, visibleColumns, safelyUpdateColumnOrder]);

  // Safely update column order ensuring symbol is always at position 0
  const handleResetColumnsToUserDefaults = async () => {
    if (!columnsInitialized) {
      alert('❌ Column preferences are still loading.\n\nPlease wait for the page to finish initializing and try again.');
      return;
    }

    logger.debug('[AggregatedTickers] Resetting columns to saved defaults');
    try {
      const config = await getDefaultColumnConfig('tickers');
      clearColumnStorage();
      updateColumnPreferencesState(config.visibleColumns, config.columnOrder);
    } catch (error) {
      logger.error('Failed to reset to saved default column configuration', error);
      clearColumnStorage();
      updateColumnPreferencesState(defaultVisibleColumns, defaultColumnOrder);
    }
  };

  const handleResetColumnsToSystemDefaults = async () => {
    if (!columnsInitialized) {
      alert('❌ Column preferences are still loading.\n\nPlease wait for the page to finish initializing and try again.');
      return;
    }
    setShowResetDefaultModal(true);
  };

  const handleSaveColumnsAsDefault = () => {
    logger.debug('[AggregatedTickers] Save columns as default clicked');

    if (!columnsInitialized) {
      alert('❌ Column preferences are still loading.\n\nPlease wait for the page to finish initializing and try again.');
      return;
    }

    if (!visibleColumns || Object.keys(visibleColumns).length === 0) {
      alert('❌ No column configuration found to save.\n\nPlease wait for the page to fully load and try again.');
      return;
    }

    if (!columnOrder || columnOrder.length === 0) {
      alert('❌ No column order found to save.\n\nPlease wait for the page to fully load and try again.');
      return;
    }

    setShowSaveDefaultModal(true);
  };

  const confirmResetColumnsToSystemDefaults = async () => {
    setShowResetDefaultModal(false);
    logger.debug('[AggregatedTickers] Resetting columns to system defaults');
    try {
      const config = await resetToDefaultColumnConfig('tickers');
      clearColumnStorage();
      updateColumnPreferencesState(config.visibleColumns, config.columnOrder);
    } catch (error) {
      logger.error('Failed to reset column configuration to system defaults', error);
      alert('❌ Failed to reset to system default columns.\n\nPlease try again.\n\nError: ' + error.message);
    }
  };

  const confirmSaveColumnsAsDefault = async () => {
    setShowSaveDefaultModal(false);
    try {
      logger.debug('[AggregatedTickers] Saving column preferences', { visibleColumns, columnOrder });
      await saveAsDefaultColumnConfig('tickers', visibleColumns, columnOrder);
      const updatedConfig = await getDefaultColumnConfig('tickers');
      clearColumnStorage();
      updateColumnPreferencesState(updatedConfig.visibleColumns, updatedConfig.columnOrder);
      logger.info('[AggregatedTickers] Column configuration saved as default');
    } catch (error) {
      logger.error('Failed to save column preferences as default', error);
      alert('❌ Failed to save column preferences as default.\n\nPlease try again.\n\nError: ' + error.message);
    }
  };

  // Add filter state
  const [showFilters, setShowFilters] = useState(() => {
    const savedShowFilters = localStorage.getItem('showFilters-allTickers');
    return savedShowFilters ? JSON.parse(savedShowFilters) : false;
  });
  
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('filters-allTickers');
    return savedFilters ? JSON.parse(savedFilters) : {
      category: '',
      symbol: '',
      minPrice: '',
      maxPrice: '',
      minValue: '',
      maxValue: '',
      gainStatus: 'all', // 'gainers', 'losers', 'all'
      positionSize: 'all', // 'large', 'medium', 'small', 'all'
    };
  });

  const summaryTotalValue = summary.total_value;

  const applyFilters = useCallback((tickersData, currentFilters = {}) => {
    if (!Array.isArray(tickersData)) {
      return [];
    }

    const filtersToApply = currentFilters || {};

    let result = tickersData.filter(ticker => {
      if (ticker.shares === undefined) {
        return true;
      }
      return ticker.shares >= 0.0001;
    });

    logger.debug('Tickers with in_multiple_accounts', {
      symbols: result.filter(t => t.in_multiple_accounts).map(t => t.symbol),
      count: result.filter(t => t.in_multiple_accounts).length
    });

    const sampleTickers = result.slice(0, 5);
    if (sampleTickers.length) {
      logger.debug('Sample ticker account info',
        sampleTickers.map(t => ({
          symbol: t.symbol,
          account_id: t.account_id,
          account_name: t.account_name,
          in_multiple_accounts: t.in_multiple_accounts
        }))
      );
    }

    const getCategoryValue = (category) => {
      if (!category) return '';
      if (typeof category === 'string') return category;
      if (typeof category === 'object') return category.name || '';
      return '';
    };

    if (filtersToApply.category) {
      result = result.filter(ticker => getCategoryValue(ticker.category) === filtersToApply.category);
    }

    if (filtersToApply.symbol) {
      const symbolFilter = filtersToApply.symbol.toLowerCase();
      result = result.filter(ticker =>
        ticker.symbol.toLowerCase().includes(symbolFilter) ||
        (ticker.name && ticker.name.toLowerCase().includes(symbolFilter))
      );
    }

    if (filtersToApply.minPrice && !isNaN(parseFloat(filtersToApply.minPrice))) {
      const minPrice = parseFloat(filtersToApply.minPrice);
      result = result.filter(ticker => ticker.current_price >= minPrice);
    }

    if (filtersToApply.maxPrice && !isNaN(parseFloat(filtersToApply.maxPrice))) {
      const maxPrice = parseFloat(filtersToApply.maxPrice);
      result = result.filter(ticker => ticker.current_price <= maxPrice);
    }

    if (filtersToApply.minValue && !isNaN(parseFloat(filtersToApply.minValue))) {
      const minValue = parseFloat(filtersToApply.minValue);
      result = result.filter(ticker => ticker.current_value >= minValue);
    }

    if (filtersToApply.maxValue && !isNaN(parseFloat(filtersToApply.maxValue))) {
      const maxValue = parseFloat(filtersToApply.maxValue);
      result = result.filter(ticker => ticker.current_value <= maxValue);
    }

    if (filtersToApply.gainStatus === 'gainers') {
      result = result.filter(ticker => ticker.total_gain > 0);
    } else if (filtersToApply.gainStatus === 'losers') {
      result = result.filter(ticker => ticker.total_gain < 0);
    }

    if (filtersToApply.positionSize && filtersToApply.positionSize !== 'all') {
      result = result.filter(ticker => {
        if (!ticker.current_value || !summaryTotalValue) {
          return true;
        }

        const positionPercentage = (ticker.current_value / summaryTotalValue) * 100;
        if (filtersToApply.positionSize === 'large') {
          return positionPercentage >= 5;
        }
        if (filtersToApply.positionSize === 'medium') {
          return positionPercentage >= 1 && positionPercentage < 5;
        }
        if (filtersToApply.positionSize === 'small') {
          return positionPercentage < 1;
        }
        return true;
      });
    }

    return result;
  }, [summaryTotalValue]);

  const filteredTickers = useMemo(() => {
    if (!tickers.length) {
      return [];
    }
    return applyFilters(tickers, filters);
  }, [tickers, filters, applyFilters]);

  // Reference to the search input field
  const searchInputRef = React.useRef(null);
  const tickerTableRef = React.useRef(null);
  const isFetchingRef = React.useRef(false);
  const pendingFetchRef = React.useRef(false);
  const pendingFetchOptionsRef = React.useRef(null);
  const fetchTickersRef = React.useRef(null);

  const hasMultiAccountTickers = !loading && tickers.some(ticker => ticker.in_multiple_accounts);
  const hasVisibleMultiAccountTickers = !loading && filteredTickers.some(ticker => ticker.in_multiple_accounts);
  
  // Add keyboard shortcut for searching
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+F or / key focuses search and expands filters
      if ((e.ctrlKey && e.key === 'f') || e.key === '/') {
        // Only if we're not already in an input field
        if (!/^(input|textarea)$/i.test(document.activeElement.tagName)) {
          e.preventDefault();
          if (!showFilters) {
            setShowFilters(true);
          }
          // Wait for collapse animation if filters just opened
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, showFilters ? 0 : 150);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showFilters]);
  
  // Save filter state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('showFilters-allTickers', JSON.stringify(showFilters));
  }, [showFilters]);
  
  useEffect(() => {
    localStorage.setItem('filters-allTickers', JSON.stringify(filters));
  }, [filters]);
  
  // Extract unique categories for filter dropdown
  const [categories, setCategories] = useState([]);
  useEffect(() => {
    if (tickers.length > 0) {
      // Handle both simple string categories and object categories with name/id fields
      const extractCategoryValue = (category) => {
        if (!category) return null;
        if (typeof category === 'string') return category;
        if (typeof category === 'object') return category.name || null;
        return null;
      };
      
      const uniqueCategories = [...new Set(
        tickers.map(ticker => extractCategoryValue(ticker.category))
      )].filter(Boolean).sort();
      
      setCategories(uniqueCategories);
    }
  }, [tickers]);

  // Check URL parameters for filters (e.g., ?symbol=AAPL)
  useEffect(() => {
    if (window.location.search) {
      const searchParams = new URLSearchParams(window.location.search);
      const symbolParam = searchParams.get('symbol');
      
      if (symbolParam) {
        setFilters(prevFilters => ({
          ...prevFilters,
          symbol: symbolParam
        }));
        setShowFilters(true);
      }
    }
  }, []);

  useEffect(() => {
    // Use the ref (kept in sync below) rather than fetchTickers directly, matching the
    // other call sites in this file — avoids re-subscribing this mount-only effect.
    fetchTickersRef.current?.();
  }, []);

  useEffect(() => {
    const handleTickerUpdateEvent = (event) => {
      logger.debug('AggregatedTickers: Handling global tickerUpdate event', {
        source: event?.detail?.source,
        time: event?.detail?.time
      });

      if (fetchTickersRef.current) {
        fetchTickersRef.current({ showSpinner: false });
      }
    };

    window.addEventListener('tickerUpdate', handleTickerUpdateEvent);
    return () => {
      window.removeEventListener('tickerUpdate', handleTickerUpdateEvent);
    };
  }, []);
  
  const fetchTickers = async (options = {}) => {
    const { showSpinner = true } = options;

    if (isFetchingRef.current) {
      pendingFetchRef.current = true;
      pendingFetchOptionsRef.current = { ...options };
      logger.debug('AggregatedTickers: fetchTickers already running, scheduling follow-up refresh');
      return;
    }

    isFetchingRef.current = true;

    if (showSpinner) {
      setTableLoading(true);
    }

    setError('');
    
    try {
      const response = await getAggregatedTickers();
      
      // VERBOSE DEBUG: Log the complete raw API response structure
      logger.debug('===== COMPLETE API RESPONSE =====');
      logger.debug('Response data', response.data);
      logger.debug('Checking account fields in first few tickers');
      if (response.data.tickers && response.data.tickers.length > 0) {
        response.data.tickers.slice(0, 5).forEach((ticker, i) => {
          logger.debug(`Ticker #${i} (${ticker.symbol})`, {
            account_id: ticker.account_id,
            account_name: ticker.account_name,
            has_account_info: Boolean(ticker.account_id),
            in_multiple_accounts: ticker.in_multiple_accounts,
            all_keys: Object.keys(ticker)
          });
        });
      }

      // Log summary data for debugging YTD issue
      logger.debug('Summary data from API', response.data.summary);
      
      const tickersData = response.data.tickers || [];
      
      // Find the most recent ticker timestamp to use for lastRefreshTime
      let mostRecentDate = null;
      if (tickersData && tickersData.length > 0) {
        for (const ticker of tickersData) {
          if (ticker.last_updated) {
            try {
              const tickerDate = new Date(ticker.last_updated);
              if (!isNaN(tickerDate) && tickerDate <= new Date()) {
                if (!mostRecentDate || tickerDate > mostRecentDate) {
                  mostRecentDate = tickerDate;
                }
              }
            } catch (e) {
              logger.error(`Error parsing ticker timestamp for ${ticker.symbol}`, e);
            }
          }
        }
      }
      
      // Set lastRefreshTime based on ticker data or response
      if (mostRecentDate) {
        logger.debug('Setting lastRefreshTime from ticker timestamp', { mostRecentDate });
        setLastRefreshTime(mostRecentDate);
      } else if (response.data.latest_update_time) {
        logger.debug('Setting lastRefreshTime from response data', {
          latestUpdateTime: response.data.latest_update_time
        });
        try {
          const updateTime = new Date(response.data.latest_update_time);
          if (!isNaN(updateTime) && updateTime <= new Date()) {
            setLastRefreshTime(updateTime);
          }
        } catch (e) {
          logger.error('Error parsing timestamp from response data', e);
        }
      }

      // Update market status if included in the response
      if (response.data && response.data.market_closed !== undefined) {
        const nextMarketInfo = {
          market_closed: response.data.market_closed,
          market_info: response.data.market_info || {}
        };
        logger.debug('Setting marketInfo from tickers response', nextMarketInfo);
        setMarketInfo(nextMarketInfo);
      }

      // The API now includes the in_multiple_accounts flag, so we don't need to calculate it
      logger.debug('Tickers marked as multi-account by API', {
        count: tickersData.filter(t => t.in_multiple_accounts).length
      });
      
      // Preserve locally-edited fields (shields, category) from current tickers state --
      // see mergeTickersPreservingLocalFields for why this must not be a wholesale overwrite.
      const currentTickers = tickers || [];
      const tickersWithShields = mergeTickersPreservingLocalFields(tickersData, currentTickers);

      const tickersWithThemeOverrides = applyThemeOverridesToTickers(tickersWithShields);
      const tickersWithAllOverrides = applyBaseOverridesToTickers(tickersWithThemeOverrides);
      setTickers(tickersWithAllOverrides);
      try { localStorage.setItem('tickers_count', String(tickersWithAllOverrides.length)); } catch (e) {}
      window.dispatchEvent(new CustomEvent('tickers:badge', { detail: { count: tickersWithAllOverrides.length } }));
      
      // Check if summary has YTD data, if not, calculate it manually
      const summaryFromApi = response.data.summary || {
        total_value: 0,
        total_cash: 0,
        today_gain_amount: 0,
        today_gain_percentage: 0,
        total_gain_amount: 0,
        total_gain_percentage: 0,
        ytd_gain_amount: 0,
        ytd_gain_percentage: 0,
        previous_day_balance: null
      };
      
      // If YTD data is missing or zero in API response, calculate it from the tickers
      if (!summaryFromApi.ytd_gain_amount && tickersData.some(ticker => ticker.ytd_gain_amount)) {
        logger.debug('YTD data missing in API summary, calculating from tickers');
        const ytd_gain_amount = tickersData.reduce((sum, ticker) => sum + (ticker.ytd_gain_amount || 0), 0);
        
        // Calculate YTD starting balance
        const ytdStartingBalance = tickersData.reduce((sum, ticker) => {
          const currentValue = ticker.current_value || 0;
          const ytdGain = ticker.ytd_gain_amount || 0;
          return sum + (currentValue - ytdGain);
        }, 0);
        
        // Calculate percentage if we have a starting balance
        const ytd_gain_percentage = ytdStartingBalance > 0 ? (ytd_gain_amount / ytdStartingBalance) * 100 : 0;
        
        summaryFromApi.ytd_gain_amount = ytd_gain_amount;
        summaryFromApi.ytd_gain_percentage = ytd_gain_percentage;
        
        logger.debug('Calculated YTD data', {
          ytd_gain_amount,
          ytd_gain_percentage,
          ytdStartingBalance
        });
      }
      
      setSummary(summaryFromApi);
    } catch (err) {
      logger.error('Error fetching aggregated tickers', err);
      setError('Failed to load tickers. Please try again.');
    } finally {
      setLoading(false);
      setTableLoading(false);
      isFetchingRef.current = false;

      if (pendingFetchRef.current) {
        const pendingOptions = pendingFetchOptionsRef.current || {};
        pendingFetchRef.current = false;
        pendingFetchOptionsRef.current = null;
        fetchTickers({ ...pendingOptions, showSpinner: false });
      }
    }
  };

  fetchTickersRef.current = fetchTickers;

  const handleRefreshPrices = async () => {
    setRefreshLoading(true);
    try {
      const updateResponse = await updateTickerPrices(true);
      logger.debug('Price update response', updateResponse.data);
      
      // Update market status if available
      if (updateResponse.data && updateResponse.data.market_closed !== undefined) {
        const refreshedMarketInfo = {
          market_closed: updateResponse.data.market_closed,
          market_info: updateResponse.data.market_info || {}
        };
        setMarketInfo(refreshedMarketInfo);
      }
      
      // Reset any previous errors
      setError(null);
      
      // Check if there are updated tickers in the data we can use directly
      if (updateResponse.data.updated_tickers && 
          updateResponse.data.updated_tickers.length > 0 && 
          tickers.length > 0) {
        
        logger.debug('Directly updating ticker prices from response data');
        
        // Create a map of ticker IDs to updated ticker data for quick lookup
        const updatedTickersMap = {};
        updateResponse.data.updated_tickers.forEach(updatedTicker => {
          if (updatedTicker.ticker_id) {
            updatedTickersMap[updatedTicker.ticker_id] = updatedTicker;
          }
        });
        
        // Update the tickers with new price data
        const updatedTickers = tickers.map(ticker => {
          const updatedTicker = updatedTickersMap[ticker.ticker_id];
          
          if (updatedTicker) {
            // Use effective price (manual price if set, otherwise current price) for all calculations
            const effectivePrice = updatedTicker.effective_price || updatedTicker.current_price;
            
            // Calculate the new current value based on number of shares using effective price
            const newCurrentValue = effectivePrice * ticker.shares;
            // Calculate the new today's gain amount using effective price
            const previousClose = updatedTicker.previous_close || ticker.pre_close;
            const todayGainAmount = previousClose ? 
              (effectivePrice - previousClose) * ticker.shares : 0;
            // Calculate today's gain percentage using effective price
            const todayGainPct = previousClose && previousClose !== 0 ? 
              ((effectivePrice - previousClose) / previousClose) * 100 : 0;
            // Update total gain calculations using effective price
            const totalGainAmount = ticker.cost_basis ? 
              (effectivePrice - ticker.cost_basis) * ticker.shares : 0;
            const totalGainPct = ticker.cost_basis && ticker.cost_basis !== 0 ? 
              ((effectivePrice - ticker.cost_basis) / ticker.cost_basis) * 100 : 0;
            // Return updated ticker with new values, preserving shields
            return {
              ...ticker,
              current_price: updatedTicker.current_price,
              pre_close: previousClose,
              previous_close: previousClose,  // Add previous_close for frontend compatibility
              current_value: newCurrentValue,
              today_gain_amount: todayGainAmount,
              today_gain_percentage: todayGainPct,
              total_gain_amount: totalGainAmount,
              total_gain_percentage: totalGainPct,
              last_updated: updatedTicker.last_updated,
              shields: ticker.shields // preserve shields property
            };
          }
          
          return ticker;
        });
        
        // Update the tickers in the component state
        const tickersWithThemeOverrides = applyThemeOverridesToTickers(updatedTickers);
        const tickersWithAllOverrides = applyBaseOverridesToTickers(tickersWithThemeOverrides);

        setTickers(tickersWithAllOverrides);

        // Update the summary data
        updateSummaryFromTickers(tickersWithThemeOverrides);
        
        // Update the last refresh time using server timestamp if available
        const serverTimestamp = updateResponse.data.latest_update_time;
        const currentTime = serverTimestamp ? new Date(serverTimestamp) : new Date();
        setLastRefreshTime(currentTime);
        // Manually dispatch the tickerUpdate event to ensure it's triggered
        window.dispatchEvent(new CustomEvent('tickerUpdate', { 
          detail: { 
            time: currentTime, 
            source: 'AggregatedTickers',
            serverTimestamp: !!serverTimestamp
          } 
        }));
        
        // Fetch complete data in the background for full refresh
        fetchTickers().catch(err => {
          logger.error('Error in background data refresh', err);
        });
      } else {
        // Fall back to fetching all data if we can't update directly
        await fetchTickers();
        
        // Update the last refresh time using server timestamp if available
        const serverTimestamp = updateResponse.data.latest_update_time;
        const currentTime = serverTimestamp ? new Date(serverTimestamp) : new Date();
        setLastRefreshTime(currentTime);
        
        // Manually dispatch the tickerUpdate event to ensure it's triggered
        window.dispatchEvent(new CustomEvent('tickerUpdate', { 
          detail: { 
            time: currentTime, 
            source: 'AggregatedTickers',
            serverTimestamp: !!serverTimestamp
          } 
        }));
      }
      
      // Reapply URL parameters if they exist
      if (window.location.search) {
        const searchParams = new URLSearchParams(window.location.search);
        const symbolParam = searchParams.get('symbol');
        
        if (symbolParam) {
          setFilters(prevFilters => ({
            ...prevFilters,
            symbol: symbolParam
          }));
        }
      }
    } catch (err) {
      logger.error('Error refreshing ticker data', err);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setRefreshLoading(false);
    }
  };

  const handleRefreshShields = async () => {
    if (shieldsRefreshing) return;
    setShieldsRefreshing(true);
    try {
      const response = await getAllAccountsWithAggregate();
      const accounts = response?.data?.accounts || [];
      const accountIds = accounts
        .map(account => account?.account_id)
        .filter(accountId => accountId !== undefined && accountId !== null);

      accountIds.forEach((accountId) => {
        try {
          localStorage.removeItem(`strategyBadgesCache-${accountId}`);
          localStorage.removeItem(`tickerShieldsCache-${accountId}`);
        } catch (error) {
          logger.warn('[AggregatedTickers] Failed to clear shields/strategy cache', error);
        }
      });

      if (accountIds.length > 0) {
        await Promise.all(accountIds.map(accountId => recalculateAccountShields(accountId)));
      }

      setTableLoading(true);

      if (fetchTickersRef.current) {
        await fetchTickersRef.current({ showSpinner: true });
      } else {
        await fetchTickers({ showSpinner: true });
      }
    } catch (error) {
      logger.warn('[AggregatedTickers] Failed to refresh shields', error);
    } finally {
      setTableLoading(false);
      setShieldsRefreshing(false);
    }
  };

  // Helper function to calculate summary data from tickers
  const updateSummaryFromTickers = useCallback((tickerData) => {
    try {
      if (!tickerData || tickerData.length === 0) {
        const emptySummary = {
          total_value: 0,
          total_cash: 0,
          today_gain_amount: 0,
          today_gain_percentage: 0,
          total_gain_amount: 0,
          total_gain_percentage: 0,
          ytd_gain_amount: 0,
          ytd_gain_percentage: 0,
          previous_day_balance: null
        };
        setSummary(emptySummary);
        return emptySummary;
      }

      // Calculate total value of all tickers
      const totalValue = tickerData.reduce((sum, ticker) => sum + (ticker.current_value || 0), 0);

      // Calculate total cash
      const totalCash = tickerData.reduce((sum, ticker) => {
        // Add cash balance if this is a cash ticker
        if (ticker.is_cash) {
          return sum + (ticker.current_value || 0);
        }
        return sum;
      }, 0);

      // Calculate today's gain
      const todayGainAmount = tickerData.reduce((sum, ticker) => sum + (ticker.today_gain_amount || 0), 0);
      const todayGainPercentage = totalValue ? (todayGainAmount / totalValue) * 100 : 0;

      // Calculate total gain
      const totalGainAmount = tickerData.reduce((sum, ticker) => sum + (ticker.total_gain || 0), 0);
      const totalGainPercentage = totalValue ? (totalGainAmount / totalValue) * 100 : 0;

      // Add YTD gain calculation
      let ytd_gain_amount = 0;
      let ytd_gain_percentage = 0;

      // Check if there are any tickers with YTD data
      const hasYtdData = tickerData.some(ticker => ticker.ytd_gain_amount !== undefined && ticker.ytd_gain_amount !== null);

      // If at least one ticker has ytd_gain_amount, use it for calculation
      if (hasYtdData) {
        logger.debug('Found YTD data in tickers');
        ytd_gain_amount = tickerData.reduce((sum, ticker) => sum + (ticker.ytd_gain_amount || 0), 0);

        // Calculate YTD starting balance by subtracting YTD gain from current value
        const ytdStartingBalance = tickerData.reduce((sum, ticker) => {
          const currentValue = ticker.current_value || 0;
          const ytdGain = ticker.ytd_gain_amount || 0;
          return sum + (currentValue - ytdGain);
        }, 0);

        // Calculate percentage if we have a starting balance
        if (ytdStartingBalance > 0) {
          ytd_gain_percentage = (ytd_gain_amount / ytdStartingBalance) * 100;
        }

        logger.debug('Calculated YTD values', {
          ytd_gain_amount,
          ytd_gain_percentage,
          ytdStartingBalance
        });
      } else {
        logger.debug('No YTD data found in tickers');
      }

      // Update summary state
      const newSummary = {
        total_value: totalValue,
        total_cash: totalCash,
        today_gain_amount: todayGainAmount,
        today_gain_percentage: todayGainPercentage,
        total_gain_amount: totalGainAmount,
        total_gain_percentage: totalGainPercentage,
        ytd_gain_amount,
        ytd_gain_percentage,
        previous_day_balance: null
      };

      logger.debug('Setting summary with YTD data', {
        ytd_gain_amount: newSummary.ytd_gain_amount,
        ytd_gain_percentage: newSummary.ytd_gain_percentage
      });

      setSummary(newSummary);
      return newSummary;
    } catch (err) {
      logger.error('Error updating summary', err);
      const fallbackSummary = {
        total_value: 0,
        total_cash: 0,
        today_gain_amount: 0,
        today_gain_percentage: 0,
        total_gain_amount: 0,
        total_gain_percentage: 0,
        ytd_gain_amount: 0,
        ytd_gain_percentage: 0,
        previous_day_balance: null
      };
      setSummary(fallbackSummary);
      return fallbackSummary;
    }
  }, []);

  const deriveMostRecentUpdateTime = useCallback((tickerData, fallbackTimestamp) => {
    let mostRecentDate = null;
    const now = new Date();

    if (Array.isArray(tickerData)) {
      tickerData.forEach(ticker => {
        if (ticker && ticker.last_updated) {
          try {
            const tickerDate = new Date(ticker.last_updated);
            if (!isNaN(tickerDate) && tickerDate <= now) {
              if (!mostRecentDate || tickerDate > mostRecentDate) {
                mostRecentDate = tickerDate;
              }
            }
          } catch (error) {
            logger.error('Error parsing ticker timestamp during polling update', error);
          }
        }
      });
    }

    if (!mostRecentDate && fallbackTimestamp) {
      try {
        const fallbackDate = new Date(fallbackTimestamp);
        if (!isNaN(fallbackDate)) {
          mostRecentDate = fallbackDate;
        }
      } catch (error) {
        logger.error('Error parsing fallback timestamp during polling update', error);
      }
    }

    return mostRecentDate;
  }, []);

  const applyPollingUpdate = useCallback((updateData = {}) => {
    const updatedTickers = Array.isArray(updateData.updated_tickers) ? updateData.updated_tickers : null;
    const hasTickerData = Boolean(updatedTickers && updatedTickers.length);
    const hasSummary = Boolean(updateData.summary);
    const hasMarketStatus = updateData.market_closed !== undefined || updateData.market_info !== undefined;
    const hasTimestamp = Boolean(updateData.latest_update_time || updateData.time);

    if (hasMarketStatus) {
      const nextMarketInfo = {
        market_closed: updateData.market_closed,
        market_info: updateData.market_info || {}
      };
      setMarketInfo(nextMarketInfo);
    }

    if (!hasTickerData && !hasSummary && !hasTimestamp && !hasMarketStatus) {
      return false;
    }

    if (hasTickerData) {
      const currentTickers = tickers || [];
      const tickersWithShields = mergeTickersPreservingLocalFields(updatedTickers, currentTickers);

      const tickersWithThemeOverrides = applyThemeOverridesToTickers(tickersWithShields);
      const tickersWithAllOverrides = applyBaseOverridesToTickers(tickersWithThemeOverrides);
      setTickers(tickersWithAllOverrides);

      if (hasSummary) {
        setSummary(updateData.summary);
      } else {
        updateSummaryFromTickers(tickersWithThemeOverrides);
      }

      const refreshTimestamp = deriveMostRecentUpdateTime(
        updatedTickers,
        updateData.latest_update_time || updateData.time
      );

      if (refreshTimestamp) {
        setLastRefreshTime(refreshTimestamp);
      }

      setLoading(false);
      setTableLoading(false);

    } else {
      if (hasSummary) {
        setSummary(updateData.summary);
      }

      const refreshTimestamp = deriveMostRecentUpdateTime(
        null,
        updateData.latest_update_time || updateData.time
      );

      if (refreshTimestamp) {
        setLastRefreshTime(refreshTimestamp);
      }

      setLoading(false);
      setTableLoading(false);
    }

    return true;
  }, [tickers, updateSummaryFromTickers, deriveMostRecentUpdateTime]);

  const handleTickerUpdate = async (updatedTickers) => {
    try {
      logger.debug('handleTickerUpdate - received updatedTickers', updatedTickers);
      
      // Handle both single ticker updates (like rating changes) and array updates
      if (Array.isArray(updatedTickers)) {
        // Update the tickers in the local state with the new risk data
        setTickers(prevTickers => {
          const updatedTickersMap = {};
          updatedTickers.forEach(updatedTicker => {
            const key = `${updatedTicker.symbol}-${updatedTicker.account_id}`;
            updatedTickersMap[key] = updatedTicker;
          });

          const mergedTickers = prevTickers.map(ticker => {
            const key = `${ticker.symbol}-${ticker.account_id}`;
            const updatedTicker = updatedTickersMap[key];

            if (updatedTicker) {
              const nextCategory = Object.prototype.hasOwnProperty.call(updatedTicker, 'category')
                ? updatedTicker.category
                : (Object.prototype.hasOwnProperty.call(updatedTicker, 'category_id') ||
                  Object.prototype.hasOwnProperty.call(updatedTicker, 'category_name'))
                  ? (updatedTicker.category_id
                    ? {
                      category_id: updatedTicker.category_id,
                      name: updatedTicker.category_name || ticker.category?.name || ''
                    }
                    : null)
                  : ticker.category;

              return {
                ...ticker,
                shield_risk_percentage: updatedTicker.shield_risk_percentage,
                shield_risk_amount: updatedTicker.shield_risk_amount,
                armor: updatedTicker.armor,
                base_enabled: updatedTicker.base_enabled !== undefined ? updatedTicker.base_enabled : ticker.base_enabled,
                theme_enabled: updatedTicker.theme_enabled !== undefined ? updatedTicker.theme_enabled : ticker.theme_enabled,
                accounts: updatedTicker.accounts !== undefined ? updatedTicker.accounts : ticker.accounts,
                category: nextCategory
              };
            }
            return ticker;
          });

          // Apply theme and base overrides to the merged tickers
          const tickersWithThemeOverrides = applyThemeOverridesToTickers(mergedTickers);
          return applyBaseOverridesToTickers(tickersWithThemeOverrides);
        });
      } else {
        // Single ticker update (e.g., rating change)
        setTickers(prevTickers => {
          const mergedTickers = prevTickers.map(ticker =>
            ticker.ticker_id === updatedTickers.ticker_id ? updatedTickers : ticker
          );
          // Apply theme and base overrides
          const tickersWithThemeOverrides = applyThemeOverridesToTickers(mergedTickers);
          return applyBaseOverridesToTickers(tickersWithThemeOverrides);
        });
      }

    } catch (err) {
      logger.error('Error updating ticker data', err);
    }
  };

  // Calculate standard size as 1/10 of total value
  const calculateStandardSize = () => {
    const totalValue = calculateTotalValue();
    return totalValue / 10;
  };

  const calculateTotalValue = () => {
    const value = summary.total_value;
    return isNaN(value) ? 0 : (value || 0);
  };

  
  const getCashBalance = () => {
    const cash = summary.total_cash;
    return isNaN(cash) ? 0 : (cash || 0);
  };

  const getPreviousBalance = () => {
    if (aggregatedPrevBalance !== null && aggregatedPrevBalance !== undefined) {
      return aggregatedPrevBalance;
    }
    if (summary.previous_day_balance !== null && summary.previous_day_balance !== undefined) {
      return summary.previous_day_balance;
    }
    if (summary.total_value !== null && summary.total_value !== undefined &&
        summary.today_gain_amount !== null && summary.today_gain_amount !== undefined) {
      return summary.total_value - summary.today_gain_amount;
    }
    return null;
  };

  // Get seasonal background color with transparency
  const getSeasonalBackground = () => {
    if (!weatherThemeEnabled || !selectedWeatherTheme) {
      return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
    
    const opacity = isDark ? 0.2 : 0.1; // Higher opacity for dark mode
    
    switch (selectedWeatherTheme) {
      case 'spring':
        return `rgba(40, 167, 69, ${opacity})`; // Green
      case 'summer':
        return `rgba(23, 162, 184, ${opacity})`; // Blue
      case 'autumn':
      case 'fall':
        return `rgba(255, 193, 7, ${opacity})`; // Orange
      case 'winter':
        return isDark ? 'rgba(248, 249, 250, 0.1)' : 'rgba(108, 117, 125, 0.1)'; // Gray
      default:
        return isDark ? 'rgba(0, 123, 255, 0.15)' : 'rgba(0, 123, 255, 0.05)';
    }
  };

  // Fetch available tickers for transaction modal
  useEffect(() => {
    const fetchAvailableTickers = async () => {
      try {
        const response = await getTickers();
        setAvailableTickers(response.data);
      } catch (err) {
        logger.error('Error fetching available tickers', err);
      }
    };

    fetchAvailableTickers();
  }, []);

  // Handle initiating a transaction from the ticker table
  const handleInitiateTransaction = (transactionData) => {
    setTransactionInitialData(transactionData);
    
    // If account_id is already available in the ticker data (from sub-tickers), use it directly
    if (transactionData.ticker.account_id) {
      logger.debug('Using preloaded account information', {
        accountId: transactionData.ticker.account_id,
        accountName: transactionData.ticker.account_name
      });
      setSelectedAccountId(transactionData.ticker.account_id);
      setShowTransactionModal(true);
      return;
    }
    
    // For parent ticker rows, follow the existing flow to determine which account to use
    const tickerSymbol = transactionData.ticker.symbol;
    
    // First, check if the tickers data contains account information
    const hasAccountInfo = tickers.some(t => t.account_id && t.account_name);
    
    if (!hasAccountInfo) {
      // If no account info in tickers data, we'll need to prompt the user to select an account
      // in the transaction modal
      logger.debug('No account information available in ticker data');
      setSelectedAccountId(null);
      setShowTransactionModal(true);
      return;
    }
    
    // Find all accounts that contain this ticker
    const accountsWithTicker = tickers
      .filter(t => t.symbol === tickerSymbol && t.account_id)
      .map(t => ({
        account_id: t.account_id,
        account_name: t.account_name || `Account ${t.account_id}`,
        portfolio_name: t.portfolio_name || 'Unknown Portfolio',
        shares: t.shares
      }))
      .filter((v, i, a) => a.findIndex(t => t.account_id === v.account_id) === i); // Remove duplicates
    
    setAvailableAccounts(accountsWithTicker);
    
    if (accountsWithTicker.length === 0) {
      // No accounts have this ticker yet, show transaction modal directly
      // with a null accountId (user will need to select in the modal)
      setSelectedAccountId(null);
      setShowTransactionModal(true);
    } else if (accountsWithTicker.length === 1) {
      // Only one account has this ticker, use it automatically
      setSelectedAccountId(accountsWithTicker[0].account_id);
      setShowTransactionModal(true);
    } else {
      // Multiple accounts have this ticker, show selection modal
      setShowAccountSelectionModal(true);
    }
  };

  const handleAccountSelected = (accountId) => {
    setSelectedAccountId(accountId);
    setShowAccountSelectionModal(false);
    setShowTransactionModal(true);
  };

  // Handle ticker symbol click to show transaction history
  const handleTickerClick = (tickerSymbol, accountId) => {
    setSelectedTickerSymbol(tickerSymbol);
    // In aggregated view, we don't filter by account, so pass null
    setShowTickerHistoryModal(true);
  };

  // Handle sell button click - navigate to account details and trigger quick sell
  const handleShowOrderDialog = (ticker) => {
    if (ticker && ticker.account_id) {
      // Navigate to the account details page with state to trigger quick sell
      navigate(`/account/${ticker.account_id}`, {
        state: {
          quickSellTicker: {
            symbol: ticker.symbol || ticker.ticker,
            forceSell: ticker.forceSell
          }
        }
      });
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: value
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      category: '',
      symbol: '',
      minPrice: '',
      maxPrice: '',
      minValue: '',
      maxValue: '',
      gainStatus: 'all',
      positionSize: 'all',
    });
  };

  // Add sorting state
  const [sortConfig, setSortConfig] = useState(() => {
    const savedSortConfig = localStorage.getItem('sortConfig-allTickers');
    return savedSortConfig ? JSON.parse(savedSortConfig) : { key: 'position_size', direction: 'desc' };
  });
  
  // Save sorting preference when it changes
  useEffect(() => {
    localStorage.setItem('sortConfig-allTickers', JSON.stringify(sortConfig));
  }, [sortConfig]);

  // Helper function to safely calculate values when data is loading
  const safeCalculate = (calculationFn) => {
    if (loading || tickers.length === 0 || typeof calculationFn !== 'function') {
      return 0;
    }
    try {
      return calculationFn() || 0;
    } catch (error) {
      logger.error('Error in calculation', error);
      return 0;
    }
  };

  // Use combined ticker updates (WebSocket + Polling)
  useTickerUpdates({
    enablePolling: true,
    onUpdate: (data) => {
      logger.debug('AggregatedTickers: Received ticker update', {
        hasUpdatedTickers: Boolean(data?.updated_tickers?.length > 0),
        updateCount: data?.updated_tickers?.length || 0,
        source: data?.source || 'unknown',
        hasLastUpdateTime: Boolean(data?.latest_update_time),
        isTimestampEvent: data?.message === 'Timestamp update event'
      });

      const hasTickerUpdates = Boolean(data?.updated_tickers?.length > 0);

      // applyPollingUpdate handles market-status/timestamp updates on every
      // cycle internally (it no-ops when the payload carries none of those),
      // so it always runs. The full snapshot refetch below is the expensive
      // part -- pollingCycleComplete is true on every successful poll, so
      // OR-ing it in here used to make this run every cycle regardless of
      // whether anything changed. Gate it on real ticker updates instead.
      applyPollingUpdate(data);

      if (hasTickerUpdates) {
        logger.debug(
          `AggregatedTickers: Refreshing ticker data after polling cycle (ticker updates present) from ${data?.source || 'unknown'}`
        );
        // Polling payloads may be incremental; a full fetch guarantees table consistency.
        if (fetchTickersRef.current) {
          fetchTickersRef.current({ showSpinner: false });
        } else {
          fetchTickers({ showSpinner: false });
        }
      }
    },
    onStatusChange: (status) => {
      logger.debug('AggregatedTickers: Update status changed', status);
    }
  });

  // Stable references for the callback props passed to TickerTable, so its
  // React.memo comparison isn't defeated by a fresh function identity on
  // every render (FRONTEND-005). Declared before the early return below so
  // they still run unconditionally on every render (Rules of Hooks).
  const stableHandleTickerUpdate = useStableCallback(handleTickerUpdate);
  const stableHandleInitiateTransaction = useStableCallback(handleInitiateTransaction);
  const stableHandleTickerClick = useStableCallback(handleTickerClick);
  const stableHandleShowOrderDialog = useStableCallback(handleShowOrderDialog);
  const stableHandleRefreshPrices = useStableCallback(handleRefreshPrices);
  const stableHandleSortChange = useStableCallback((key, direction) => setSortConfig({ key, direction }));

  if (error) {
    return (
      <Container>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <style>{customStyles}</style>
      {/* Message when filtering by symbol - Only show when data is loaded */}
      {!loading && filters.symbol && window.location.search.includes('symbol=') && (
        <Alert variant="primary" className="mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">
                <i className="bi bi-search me-2"></i>
                Showing instances of <strong>{filters.symbol}</strong>
              </h5>
              {filteredTickers.length > 0 && filteredTickers.some(t => t.in_multiple_accounts) && (
                <p className="mb-0 mt-2">
                  Found in {filteredTickers.filter(t => t.account_id && t.account_name).length} different accounts. 
                  <span className="text-muted ms-2">Click the expand icon next to multi-account tickers to see account instances.</span>
                </p>
              )}
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={() => {
                // Clear the symbol filter
                setFilters(prev => ({ ...prev, symbol: '' }));
                // Remove from URL
                const searchParams = new URLSearchParams(window.location.search);
                searchParams.delete('symbol');
                const newRelativePathQuery = window.location.pathname + 
                  (searchParams.toString() ? `?${searchParams.toString()}` : '');
                window.history.pushState(null, '', newRelativePathQuery);
              }}
            >
              Show All Tickers <i className="bi bi-x-circle ms-1"></i>
            </Button>
          </div>
        </Alert>
      )}

      {/* Multi-account info alert - Only show when data is loaded */}
      {/* Removed per request */}

      {/* Always show cards and table, with placeholders when loading */}
      <>
        {/* ROW OF CARDS REMOVED */}

        {/* Buttons row */}
        <Row className="mb-3">
          <Col className="d-flex flex-wrap align-items-center gap-2">
            <div className="d-flex flex-wrap align-items-center gap-2">
              {/* Analytics buttons */}
              <div className="d-flex gap-2 flex-nowrap">
                <Button
                  variant="outline-primary"
                  onClick={() => setShowBalanceHistoryModal(true)}
                  size="sm"
                  className="text-nowrap"
                  disabled={loading}
                >
                  <i className="bi bi-graph-up"></i> Balance
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowPerformanceModal(true)}
                  size="sm"
                  className="text-nowrap"
                  disabled={loading}
                >
                  <i className="bi bi-speedometer2"></i> Performance
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowPieChartModal(true)}
                  size="sm"
                  className="text-nowrap"
                  disabled={loading}
                >
                  <i className="bi bi-pie-chart"></i> Pie Chart
                </Button>
                <Button 
                  variant="outline-primary"
                  onClick={() => setShowTreeMapModal(prev => !prev)}
                  size="sm"
                  className="text-nowrap"
                  disabled={loading}
                >
                  <i className="bi bi-diagram-3"></i> Tree Map
                </Button>
              </div>

              <div
                className="ticker-quick-filter flex-grow-0"
                style={{ minWidth: '110px', maxWidth: '150px', flexBasis: '14%' }}
              >
                <i className="bi bi-search input-search-icon"></i>
                <Form.Control
                  size="sm"
                  type="text"
                  className="ps-4 pe-4"
                  value={filters.symbol}
                  onChange={(e) => handleFilterChange('symbol', e.target.value)}
                  aria-label="Quick ticker filter"
                />
                <button
                  type="button"
                  className="input-clear-button"
                  onClick={() => handleFilterChange('symbol', '')}
                  disabled={!filters.symbol}
                  aria-label="Clear quick filter"
                  title="Clear quick filter"
                >
                  <i className="bi bi-x-circle"></i>
                </button>
              </div>
            </div>

            {/* Table function buttons */}
            <div className="d-flex gap-2 flex-wrap ms-auto">
              <Button
                variant="outline-primary"
                size="sm"
                className="text-nowrap"
                onClick={handleRefreshShields}
                disabled={loading || shieldsRefreshing}
                title="Recalculate and refresh all shields"
              >
                <i className={`bi bi-arrow-repeat me-1 ${shieldsRefreshing ? 'spin' : ''}`}></i>
                Shield
              </Button>
              {/* Multi-account bulk controls */}
              {hasMultiAccountTickers && (
                <>
                  {hasVisibleMultiAccountTickers && (
                    <>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="text-nowrap"
                        onClick={() => tickerTableRef.current?.collapseAllMultiAccountRows()}
                        title="Collapse all multi-account tickers"
                      >
                        Collapse All
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="text-nowrap"
                        onClick={() => tickerTableRef.current?.expandAllMultiAccountRows()}
                        title="Expand all multi-account tickers to show account positions"
                      >
                        Expand All
                      </Button>
                    </>
                  )}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="text-nowrap"
                    onClick={() => {
                      // Toggle sort mode
                      const currentMode = localStorage.getItem('multiSortMode') === 'alphabetical' ? 'alphabetical' : 'grouped';
                      const newMode = currentMode === 'grouped' ? 'alphabetical' : 'grouped';

                      // Store in localStorage and also dispatch storage event to notify other components
                      localStorage.setItem('multiSortMode', newMode);

                      // Dispatch storage event manually since we're in the same window
                      window.dispatchEvent(new StorageEvent('storage', {
                        key: 'multiSortMode',
                        newValue: newMode,
                        url: window.location.href
                      }));

                      // Force a re-render
                      setTickers([...tickers]);
                    }}
                    title={localStorage.getItem('multiSortMode') === 'alphabetical'
                      ? 'Click to group multi-account tickers together when sorting by symbol'
                      : 'Currently grouping multi-account tickers together when sorting by symbol'}
                  >
                    {localStorage.getItem('multiSortMode') === 'alphabetical' ? 'Sort' : 'Multi'}
                  </Button>
                </>
              )}
              {/* Filters Button */}
              <Button
                variant="outline-primary"
                onClick={() => setShowFilters(!showFilters)}
                size="sm"
                className="text-nowrap"
                disabled={loading}
              >
                <i className={`bi bi-funnel${showFilters || (!loading && filteredTickers.length !== tickers.length) ? '-fill' : ''} me-1`}></i>
                Filters{!loading && filteredTickers.length !== tickers.length && 
                  <Badge bg="light" text="primary" pill className="ms-1">{filteredTickers.length}</Badge>
                }
              </Button>
              {/* Reset Columns Button */}
              <Button
                variant="outline-primary"
                size="sm"
                className="text-nowrap me-2"
                disabled={loading || !columnsInitialized}
                onClick={handleResetColumnsToUserDefaults}
              >
                Reset Cols
              </Button>
              {/* Columns Dropdown - Always show */}
              <ColumnVisibilityDropdown
                visibleColumns={visibleColumns}
                columns={Object.keys(visibleColumns)
                  .filter(key => key !== 'symbol') // Symbol always shown
                  .sort((a, b) => {
                    // Actions goes immediately before Strategy; leave every other pair's order alone
                    if (a === 'actions' && b === 'strategy') return -1;
                    if (a === 'strategy' && b === 'actions') return 1;
                    return 0;
                  })}
                onToggleColumn={(column, newValue) => {
                  setVisibleColumns(prev => ({
                    ...prev,
                    [column]: newValue
                  }));
                }}
                disabled={loading || !columnsInitialized}
              >
                <Dropdown.Divider />
                <div className="text-muted small mb-2">
                  <div>• Click column headers to sort</div>
                  <div>• Drag column headers to reorder</div>
                </div>
                <Dropdown.Divider />
                <div className="d-grid gap-1">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100"
                    disabled={!columnsInitialized}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveColumnsAsDefault();
                    }}
                  >
                    Set as Default
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="w-100"
                    disabled={!columnsInitialized}
                    onClick={handleResetColumnsToUserDefaults}
                  >
                    Reset Columns
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="w-100"
                    disabled={!columnsInitialized}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetColumnsToSystemDefaults();
                    }}
                  >
                    Reset to System Default
                  </Button>
                </div>
              </ColumnVisibilityDropdown>
            </div>
          </Col>
        </Row>

        {showTreeMapModal && (
          <div className="tickers-table-container mb-3" style={{ '--seasonal-bg': getSeasonalBackground() }}>
            <div className="table-responsive">
              <TreeMapContainer
                inline
                show={showTreeMapModal}
                onHide={() => setShowTreeMapModal(false)}
                tickers={tickers}
                cash={safeCalculate(getCashBalance)}
                previousBalance={getPreviousBalance()}
                lastRefreshTime={lastRefreshTime}
                onRefresh={handleRefreshPrices}
                marketInfo={marketInfo}
                pageKey="aggregatedTickers"
              />
            </div>
          </div>
        )}

        {/* Filter Card - Moved below the buttons row */}
        <Collapse in={showFilters}>
          <Card className="mb-4 tickers-card">
            <Card.Body className="p-2">
              <Row className="g-2">
                <Col xs={12} sm={6} md={3} className="mb-0">
                  <Form.Group>
                    <Form.Label className="small mb-0">
                      Symbol/Name
                      <span className="ms-2 text-muted">(Ctrl+F or / to search)</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search symbol or name"
                      value={filters.symbol}
                      onChange={(e) => handleFilterChange('symbol', e.target.value)}
                      size="sm"
                      ref={searchInputRef}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-0">
                  <Form.Group>
                    <Form.Label className="small mb-0">Category</Form.Label>
                    <Form.Select
                      value={filters.category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      size="sm"
                    >
                      <option value="">All Categories</option>
                      {categories.map(category => (
                        <option key={typeof category === 'object' ? category.category_id : category} value={typeof category === 'object' ? category.name : category}>
                          {typeof category === 'object' ? category.name : category}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} md={2} className="mb-0">
                  <Form.Group>
                    <Form.Label className="small mb-0">Position Size</Form.Label>
                    <Form.Select
                      value={filters.positionSize}
                      onChange={(e) => handleFilterChange('positionSize', e.target.value)}
                      size="sm"
                    >
                      <option value="all">All Sizes</option>
                      <option value="large">Large (≥5%)</option>
                      <option value="medium">Medium (1-5%)</option>
                      <option value="small">Small (≤1%)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6} md={2} className="mb-0">
                  <Form.Group>
                    <Form.Label className="small mb-0">Performance</Form.Label>
                    <Form.Select
                      value={filters.gainStatus}
                      onChange={(e) => handleFilterChange('gainStatus', e.target.value)}
                      size="sm"
                    >
                      <option value="all">All Positions</option>
                      <option value="gainers">Winners</option>
                      <option value="losers">Losers</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={12} md={2} className="mb-0 d-flex align-items-end justify-content-end">
                  <div className="d-flex gap-2">
                    <Button 
                      variant="outline-secondary" 
                      size="sm" 
                      onClick={clearFilters}
                      className="text-nowrap"
                    >
                      Reset
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowFilters(false)}
                      className="text-nowrap"
                      aria-label="Close"
                    >
                      Close <i className="bi bi-x"></i>
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Collapse>

        {/* Use TickerTable with tableLoading prop instead of a separate loading spinner */}
        <div className="tickers-table-container" style={{ '--seasonal-bg': getSeasonalBackground() }}>
          <div className="table-responsive">
            <TickerTable
              tickers={filteredTickers}
              showAccountDetails={false}
              onTickerUpdate={stableHandleTickerUpdate}
              hideColumnsDropdown={true}
              visibleColumnsFromParent={visibleColumns}
              columnOrder={columnOrder}
              onColumnOrderChange={safelyUpdateColumnOrder}
              accountCash={safeCalculate(getCashBalance)}
              onInitiateTransaction={stableHandleInitiateTransaction}
              accountStandardSize={safeCalculate(calculateStandardSize)}
              accountTotalValue={safeCalculate(calculateTotalValue)}
              sortConfig={sortConfig}
              onSortChange={stableHandleSortChange}
              tableLoading={tableLoading}
              onShieldUpdate={null}
              onTickerClick={stableHandleTickerClick}
              hideOrderButton={true}
              onShowOrderDialog={stableHandleShowOrderDialog}
              onRefresh={stableHandleRefreshPrices}
              ref={tickerTableRef}
            />
          </div>
        </div>
        
        {/* Show empty state message only when not loading and there are no tickers */}
        {!tableLoading && tickers.length === 0 && (
          <div className="text-center my-5">
            <Alert variant="info">
              <p>You don't have any investments yet.</p>
              <p>Go to your accounts to add transactions.</p>
            </Alert>
            <Button 
              as={Link}
              to="/dashboard"
              variant="primary"
            >
              Go to Portfolios
            </Button>
          </div>
        )}
      </>
      
      {/* Pie Chart Modal */}
      <PieChartModal
        show={showPieChartModal}
        onHide={() => setShowPieChartModal(false)}
        tickers={tickers}
        cash={safeCalculate(getCashBalance)}
        lastRefreshTime={lastRefreshTime}
        onRefresh={handleRefreshPrices}
      />
      
      {/* Balance History Modal */}
      <BalanceHistoryModal
        show={showBalanceHistoryModal}
        onHide={() => setShowBalanceHistoryModal(false)}
        isAggregated={true}
        currentBalance={calculateTotalValue()}
        isBalanceLoading={tableLoading}
      />
      <PerformanceChartModal
        show={showPerformanceModal}
        onHide={() => setShowPerformanceModal(false)}
        isAggregated={true}
        title="Aggregated Performance"
        currentBalance={calculateTotalValue()}
        isBalanceLoading={tableLoading}
      />

      {/* Account Selection Modal */}
      <Modal
        show={showAccountSelectionModal}
        onHide={() => setShowAccountSelectionModal(false)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This ticker exists in multiple accounts. Please select which account you want to use for this transaction:</p>
          <ListGroup>
            {availableAccounts.map(account => (
              <ListGroup.Item
                key={account.account_id}
                action
                onClick={() => handleAccountSelected(account.account_id)}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{account.account_name}</strong>
                  <div className="text-muted small">{account.portfolio_name}</div>
                </div>
                <Badge bg="info">{account.shares} shares</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAccountSelectionModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Transaction Modal */}
      {showTransactionModal && (
        <TransactionModal
          show={showTransactionModal}
          onHide={() => {
            setShowTransactionModal(false);
            setTransactionInitialData(null);
            setSelectedAccountId(null);
          }}
          accountId={selectedAccountId}
          currentCash={
            // If we have transaction data with account information, try to find the cash balance for that account
            selectedAccountId ? 
              // First, look through tickers to find the account's cash balance
              tickers.find(t => t.account_id === selectedAccountId && t.account_cash !== undefined)?.account_cash || 0
              : 0
          }
          availableTickers={availableTickers}
          setAvailableTickers={setAvailableTickers}
          onTransactionComplete={fetchTickers}
          accountTickers={
            // Use account-specific filtering when we have a selectedAccountId
            selectedAccountId 
              ? tickers.filter(t => t.account_id === selectedAccountId) 
              : []
          }
          initialData={transactionInitialData}
        />
      )}
      
      {/* Ticker Transaction History Modal */}
      <TickerTransactionHistoryModal
        show={showTickerHistoryModal}
        onHide={() => {
          setShowTickerHistoryModal(false);
          setSelectedTickerSymbol(null);
        }}
        tickerSymbol={selectedTickerSymbol}
      />

      <Modal
        show={showSaveDefaultModal}
        onHide={() => setShowSaveDefaultModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Save Column Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Save your current Aggregated Tickers column configuration as your personal default?</p>
          <p className="mb-2">This will apply whenever you open the tickers page and includes:</p>
          <ul className="mb-0">
            <li>{Object.values(visibleColumns || {}).filter(Boolean).length} visible columns</li>
            <li>The current column order</li>
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveDefaultModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSaveColumnsAsDefault}>
            Save as Default
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showResetDefaultModal}
        onHide={() => setShowResetDefaultModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Column Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Reset the Aggregated Tickers columns back to the original system defaults?</p>
          <p className="mb-0 text-muted">This clears your saved defaults for this table.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetDefaultModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmResetColumnsToSystemDefaults}>
            Reset Columns
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Fixed position feedback message */}
      {historySaveMessage && (
        <div 
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1050,
            minWidth: '300px',
            maxWidth: '500px'
          }}
        >
          <Alert 
            variant={historySaveMessage.type} 
            className="py-2 shadow" 
            dismissible 
            onClose={() => setHistorySaveMessage('')}>
            {historySaveMessage.text}
          </Alert>
        </div>
      )}
    </Container>
  );
}

export default AggregatedTickers; 
